import React from 'react';
import PhoneIcon from 'assets/svg/phone-new.svg';
import SendIcon from 'assets/svg/send.svg';
import * as Styled from '../private/styled';
import { useLocale } from 'locale';
import { PoiId } from 'utils/entities';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { TextField } from 'ds/components/input/TextField';
import { Poi } from 'utils/marketplaceRoutes';
import { createMortgageValidationFn } from 'components/madad/contact-modal/utils';
import { ContactAgentModalSource } from 'store/state/app';
import { SubmitContactAgentPayload, ContactAgentSubmitterSource } from 'store/state/app/payloads';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { noop } from 'lodash';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { SpacingScale } from 'ds/theme/spacing';
import TermsLine from 'components/terms-line';
import _ from 'lodash';
import { DeveloperContactProps } from 'components/contact/types';
import DoneIcon from 'assets/svg/done.svg';
import { Text } from 'ds/components/typography';
import { DeveloperLogo } from 'components/contact/DeveloperLogo';
import { DeveloperInfoWrapper } from '../private/styled';
import { Developers } from 'components/contact/Developers';

interface SideBarDeveloperContactProps extends DeveloperContactProps {
  id: PoiId;
  source: ContactAgentModalSource;
  sidePaddingSpacing?: SpacingScale;
  onContactAgentInputClick: (eventName: string, poi: Poi) => void;
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => void;
  handleSubmitEvent?: (hasErrors: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
}

interface State {
  userName: string;
  userPhone: string;
  formErrors: {
    phone?: string;
    message?: string;
  };
}

export const SideBarDeveloperContact: React.FC<SideBarDeveloperContactProps> = (props) => {
  const {
    id,
    poi,
    source,
    poiType,
    contactValues,
    onCallClickEvent = noop,
    handleSubmitEvent = noop,
    onContactPageSubmit,
    onContactAgentInputClick,
    isContacted,
    sidePaddingSpacing,
    phone,
    projectName,
    projectLogo,
    developers,
    isPromoted,
  } = props;

  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint < 3;
  const [ phoneNumberIsVisible, setPhoneNumberIsVisible ] = React.useState(false);
  const [ state, setState ] = React.useState<State>({ userName: contactValues.name, userPhone: contactValues.phone, formErrors: {} });

  const handlePhoneSubmit = (e: React.MouseEvent<HTMLElement>) => {
    onCallClickEvent(isMobile || phoneNumberIsVisible, { phone: state.userPhone, name: state.userName, isMortgageConsult: false, id, type: poiType }, source);

    if (!phoneNumberIsVisible && !isMobile) {
      e.preventDefault();
      setPhoneNumberIsVisible(true);
    }
  };

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, userPhone: e.currentTarget.value });
  const onMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => setState({ ...state, userName: e.currentTarget.value });

  const validate = React.useCallback(createMortgageValidationFn({
    phone: {
      invalid: t('contactAgentModal.form.phone.invalid'),
      required: t('contactAgentModal.form.phone.required'),
    },
    message: {
      required: t('contactAgentModal.form.message.required'),
    },
  }), [ t ]);

  const onSubmit = () => {
    const errors = validate({
      phone: state.userPhone,
    });
    const formValues = {
      phone: state.userPhone,
      name: state.userName,
      isMortgageConsult: false,
      type: poiType,
      id,
      source,
    };
    setState({ ...state, formErrors: errors });

    let hasErrors = false;

    if (errors.phone) {
      hasErrors = true;
    }

    handleSubmitEvent(hasErrors, formValues, source);

    if (hasErrors) return;

    onContactPageSubmit({
      key: formValues.id,
      variables: formValues,
      source: ContactAgentSubmitterSource.Form,
    });
  };

  if (isContacted) {
    return (
      <Styled.Root>
        <DoneIcon width={62} height={62} />
        <Text>{t('contactForm.contact.form.withMortgage.succeedMessage.modeB')}</Text>
      </Styled.Root>
    );
  }

  const formattedProjectName = t('projectPage.project.name', { name: projectName, isNotPromoted: !isPromoted });

  return (
    <Styled.Root sidePaddingSpacing={sidePaddingSpacing}>
      <Styled.FormTitle weight="bold">{t('contactForm.contact.form.withMortgage.mobile.title', { isContacted })}</Styled.FormTitle>
      <Styled.InfoBox>
        <DeveloperLogo developerLogo={isPromoted ? projectLogo : undefined} size={80} />
        <DeveloperInfoWrapper>
          <Styled.UserName weight="bold">{formattedProjectName}</Styled.UserName>
          <Developers developers={developers} maxWidth={270} isCentered />
        </DeveloperInfoWrapper>
      </Styled.InfoBox>
      {isPromoted && phone ? (
        <>
          <div style={{ width: '100%' }}>
          <Styled.PhoneButton
            data-auto="phone-number-button"
            size="extraLarge"
            fullWidth
            weight="medium"
            onClick={handlePhoneSubmit}
            href={createPhoneURI(phone)}
          >
            <PhoneIcon />
            {phoneNumberIsVisible ? phone : t('contactAgentModal.callButton')}
          </Styled.PhoneButton>
        </div>

          <Styled.HintTitle weight="medium">
            {t('contactForm.contact.sidebar.private.hint')}
          </Styled.HintTitle>
        </>
      ) : null}
      <Styled.FormBody>
        <TextField
          fullWidth
          data-auto="name-input"
          hasError={false}
          errorMessage={null}
          value={state.userName}
          onChange={onMessageChange}
          placeholder={t('contactForm.contact.name')}
          onClick={() => onContactAgentInputClick('property_lead_form_messege_box_click', poi)}
        />
        <TextField
          fullWidth
          value={state.userPhone}
          hasError={Boolean(state.formErrors.phone)}
          data-auto="phone-input"
          errorMessage={state.formErrors.phone}
          onChange={onPhoneChange}
          placeholder={t('contactForm.contact.form.placeholder.phone')}
          onClick={() => onContactAgentInputClick('property_lead_form_phone_text_box_click', poi)}
        />
        <Styled.SubmitButton onClick={onSubmit} data-auto="submit-lead-button">
          <SendIcon />
        </Styled.SubmitButton>
      </Styled.FormBody>
      <TermsLine />
    </Styled.Root>
  );
};
